/**
 * A report cut into the sheets it is printed on.
 *
 * A report is a tree of sections and has no pages of its own: where one page
 * ends is decided by the page breaks somebody placed and, when the document
 * asks for it, by each chapter starting afresh. This works those decisions out
 * once, so the canvas can draw one sheet of paper per page and the printed
 * file can start a page at exactly the same places.
 *
 * **A page break cuts wherever it stands.** At the top level it falls between
 * two sections. Inside a chapter it falls inside the chapter, so the chapter
 * is drawn in parts: the part before the break opens the chapter and carries
 * its heading, the part after it carries on at the top of the next sheet, and
 * a chapter inside that chapter is cut the same way, as deep as the break is.
 * Every part is still the one chapter in the tree, so selecting, moving and
 * numbering it are unaffected by where it happens to be cut. A chapter with no
 * page break anywhere inside it is never cut, and is drawn whole.
 *
 * **The same rules hold in print and in the Word export**, so the three
 * renderings of a report agree on where its pages start:
 *
 * - A page break before anything is on the first page opens that page rather
 *   than leaving a blank one in front of it. Its picture, if it has one,
 *   belongs to that first page.
 * - A cover has its page to itself: whatever follows it starts a new sheet.
 *   A page break straight after it is that same new sheet, not a blank one.
 * - So does a whole page (a full-page picture, a page imported from a PDF),
 *   on both sides: it starts a sheet of its own unless the one it would
 *   leave is still empty, and whatever follows it starts the next, under
 *   the same rule as after a cover.
 * - A chapter starts a sheet only when the document asks for it, only at the
 *   top level, and only when the sheet it would leave already holds something.
 *   A chapter straight after a page break is on its fresh page already, and
 *   breaking again would print a blank one.
 * - Two page breaks in a row give a blank sheet, on purpose: that is how an
 *   author asks for a blank page, and hiding it would make the canvas lie.
 * - A page break can make the page it opens a double page. Only that page:
 *   the next page, whatever opens it, is a single one again.
 *
 * Pure. Sections in, sheets out, nothing measured and nothing rendered.
 */

import {
  isGroup,
  type GroupSection,
  type LayerSection,
  type PageBreakSection,
  type Section,
} from '@/domain/section'
import { contains } from '@/domain/tree'

/** Kinds that are a whole page in themselves, and so never share a sheet. */
const WHOLE_PAGE_KINDS: ReadonlySet<Section['kind']> = new Set(['imagePage', 'fixedPage'])

function isWholePage(section: Section): boolean {
  return WHOLE_PAGE_KINDS.has(section.kind)
}

/** What made a sheet begin. */
export type SheetOpener =
  /** The first sheet, which nothing had to open. */
  | { readonly by: 'start' }
  /** A page break, which also brings the picture drawn behind the page. */
  | { readonly by: 'break'; readonly section: PageBreakSection }
  /** A chapter, in a document set to start every chapter on a new page. */
  | { readonly by: 'chapter' }
  /** The cover before it, which keeps its page to itself. */
  | { readonly by: 'cover' }
  /** A full-page picture, which keeps its page to itself: opening it, or ending it. */
  | { readonly by: 'picture' }

/** Part of a chapter a page break cuts, as it falls on one sheet. */
export interface SheetPart {
  readonly kind: 'part'
  readonly group: GroupSection
  /** What of the chapter is on this sheet. */
  readonly pieces: readonly SheetPiece[]
  /** Whether the chapter begins on this sheet, and so carries its heading here. */
  readonly opens: boolean
  /** Whether the chapter ends on this sheet. */
  readonly closes: boolean
}

/** What a sheet holds: whole sections, and parts of the chapters cut across it. */
export type SheetPiece = { readonly kind: 'whole'; readonly section: Section } | SheetPart

/** One sheet of paper and what is printed on it. */
export interface Sheet {
  /**
   * Stable while the document is edited, so a sheet keeps its element as
   * sections come and go. Taken from whatever opened it, which is the one
   * thing about a sheet that does not change when its contents do.
   */
  readonly key: string
  readonly opener: SheetOpener
  readonly pieces: readonly SheetPiece[]
  /**
   * Whether this sheet is a double page, twice as wide as the others.
   *
   * Only a page break can ask for one, since it is the only place an author
   * says what the page it starts looks like. A sheet a chapter or the cover
   * opened is a single page, as every sheet is unless asked otherwise.
   */
  readonly spread: boolean
}

/** The document settings that decide where pages start. */
export interface SheetRules {
  readonly chapterPageBreak: boolean
}

interface BuildingPart {
  readonly kind: 'part'
  readonly group: GroupSection
  readonly pieces: BuildingPiece[]
  readonly opens: boolean
  closes: boolean
}

type BuildingPiece = { readonly kind: 'whole'; readonly section: Section } | BuildingPart

interface Building {
  key: string
  opener: SheetOpener
  readonly pieces: BuildingPiece[]
}

/** Whether a page break stands anywhere inside a chapter, however deep. */
function cutInside(group: GroupSection): boolean {
  return group.children.some(
    (child) => child.kind === 'pageBreak' || (isGroup(child) && cutInside(child)),
  )
}

/**
 * Whether anything would be printed from these pieces.
 *
 * A section counts, and so does the part of a chapter that prints its heading.
 * The rest of a chapter carried over a page break counts only for what is in
 * it: until something lands there, the page it is on has only just begun.
 */
export function holdsAnything(pieces: readonly SheetPiece[]): boolean {
  return pieces.some(
    (piece) =>
      piece.kind === 'whole' ||
      (piece.opens && piece.group.showTitle) ||
      holdsAnything(piece.pieces),
  )
}

/** The section a piece draws: the section itself, or the chapter a part belongs to. */
export function sectionOf(piece: SheetPiece): Section {
  return piece.kind === 'whole' ? piece.section : piece.group
}

/**
 * The report as sheets, in order.
 *
 * Never empty: a report with nothing in it is still one blank sheet, since a
 * page with nothing on it is what an empty report looks like.
 */
export function sheetsOf(sections: readonly Section[], rules: SheetRules): Sheet[] {
  let sheet: Building = { key: 'start', opener: { by: 'start' }, pieces: [] }
  const sheets: Building[] = [sheet]

  /** The chapters open where the walk stands, outermost first, as parts on the current sheet. */
  let open: BuildingPart[] = []

  const here = (): BuildingPiece[] => open.at(-1)?.pieces ?? sheet.pieces

  /** A new sheet, with every chapter the cut fell inside carried on at its top. */
  const turn = (key: string, opener: SheetOpener): void => {
    sheet = { key, opener, pieces: [] }
    sheets.push(sheet)

    const carried: BuildingPart[] = []
    for (const part of open) {
      const next: BuildingPart = {
        kind: 'part',
        group: part.group,
        pieces: [],
        opens: false,
        closes: false,
      }
      ;(carried.at(-1)?.pieces ?? sheet.pieces).push(next)
      carried.push(next)
    }
    open = carried
  }

  const walk = (items: readonly Section[]): void => {
    for (const section of items) {
      if (section.kind === 'pageBreak') {
        const untouched = sheets.length === 1 && !holdsAnything(sheet.pieces)
        if (untouched && sheet.opener.by === 'start') {
          sheet.key = section.id
          sheet.opener = { by: 'break', section }
        } else {
          turn(section.id, { by: 'break', section })
        }
        continue
      }

      // A free layer takes no room in the flow, so it never starts a page: set
      // straight after a cover, it is laid over the cover's page. Nor does it
      // stand between a cover and what comes after, which still turns the page.
      if (open.length === 0 && section.kind !== 'layer') {
        const last = sheet.pieces.findLast(
          (piece) => piece.kind !== 'whole' || piece.section.kind !== 'layer',
        )
        if (last?.kind === 'whole' && last.section.kind === 'cover') {
          turn(`cover:${last.section.id}`, { by: 'cover' })
        } else if (last?.kind === 'whole' && isWholePage(last.section)) {
          turn(`after:${last.section.id}`, { by: 'picture' })
        } else if (isWholePage(section) && holdsAnything(sheet.pieces)) {
          turn(`picture:${section.id}`, { by: 'picture' })
        } else if (isGroup(section) && rules.chapterPageBreak && holdsAnything(sheet.pieces)) {
          turn(`chapter:${section.id}`, { by: 'chapter' })
        }
      }

      if (isGroup(section) && cutInside(section)) {
        const part: BuildingPart = {
          kind: 'part',
          group: section,
          pieces: [],
          opens: true,
          closes: false,
        }
        here().push(part)
        open.push(part)
        walk(section.children)

        // Whichever sheet the walk ended on, that part is where the chapter ends.
        const ending = open.pop()
        if (ending) ending.closes = true
        continue
      }

      here().push({ kind: 'whole', section })
    }
  }

  walk(sections)

  // Read off the opener once the walk is over, since the first sheet can be
  // handed a page break after it was made.
  return sheets.map((built) => ({
    ...built,
    spread: built.opener.by === 'break' && built.opener.section.spread,
  }))
}

/** Whether any sheet is a double page, so the desk has to be wide enough for one. */
export function hasSpread(sheets: readonly Sheet[]): boolean {
  return sheets.some((sheet) => sheet.spread)
}

/**
 * The free layers on a sheet, in the order they stand, however deep.
 *
 * A layer is drawn over the sheet it stands on rather than in the flow where
 * it sits (see `sections/layer`), so the sheet has to find its layers: at the
 * top level, inside a chapter drawn whole, or inside the part of a chapter
 * that fell on this sheet.
 */
export function layersOn(pieces: readonly SheetPiece[]): LayerSection[] {
  const found: LayerSection[] = []

  const visit = (section: Section): void => {
    if (section.kind === 'layer') found.push(section)
    else if (isGroup(section)) section.children.forEach(visit)
  }

  for (const piece of pieces) {
    if (piece.kind === 'whole') visit(piece.section)
    else found.push(...layersOn(piece.pieces))
  }
  return found
}

/**
 * The section a new one goes after, to stand on this sheet: the last section
 * placed on it, however deep, or the page break that opened it when nothing
 * is placed on it yet. Nothing only for the empty report's single sheet,
 * where a new section simply goes at the end.
 */
export function anchorOn(sheet: Sheet): string | null {
  const lastIn = (pieces: readonly SheetPiece[]): string | null => {
    for (let at = pieces.length - 1; at >= 0; at -= 1) {
      const piece = pieces[at]
      if (!piece) continue
      if (piece.kind === 'whole') return piece.section.id
      const inner = lastIn(piece.pieces)
      if (inner) return inner
    }
    return null
  }
  return lastIn(sheet.pieces) ?? (sheet.opener.by === 'break' ? sheet.opener.section.id : null)
}

/**
 * The sheet a section stands on, or nothing for a section on no sheet.
 *
 * A chapter cut across sheets stands on the one it opens on, which is where
 * its heading is.
 */
export function sheetHolding(sheets: readonly Sheet[], id: string): Sheet | null {
  const within = (pieces: readonly SheetPiece[]): boolean =>
    pieces.some((piece) =>
      piece.kind === 'whole'
        ? piece.section.id === id || contains(piece.section, id)
        : (piece.opens && piece.group.id === id) || within(piece.pieces),
    )
  return sheets.find((sheet) => within(sheet.pieces)) ?? null
}

/** How far short of a whole page a sheet may be measured and still count as one. */
const MEASURING_SLACK_PX = 1

/**
 * Where the printer cuts a sheet, measured from its top edge.
 *
 * A sheet is at least one page tall and grows when what is on it does not
 * fit. Printed, the overflow carries on to a new page every page height, and
 * those are the folds. The slack absorbs the fraction of a pixel a measured
 * height carries, or a sheet exactly one page tall would sometimes grow a fold
 * at its very end.
 *
 * Pure: both lengths are in the same unit, pixels as laid out.
 */
export function foldsOf(sheetHeightPx: number, pageHeightPx: number): number[] {
  if (!(pageHeightPx > 0) || !(sheetHeightPx > 0)) return []

  const folds: number[] = []
  for (let fold = pageHeightPx; fold < sheetHeightPx - MEASURING_SLACK_PX; fold += pageHeightPx) {
    folds.push(fold)
  }
  return folds
}

/** CSS pixels in a millimetre: 96 to the inch, 25.4 millimetres to the inch. */
export const PX_PER_MM = 96 / 25.4
